/* eslint-disable @typescript-eslint/no-unused-vars, check-file/filename-blocklist -- pre-existing in this legacy file; the dark-mode sweep only themes its colour literals */
import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { components } from 'react-select';
import { Check } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';

function findRootParent(data, id) {
  const item = data.find((item) => item.id === id);
  if (!item) {
    return null;
  }
  if (!item.parent_id) {
    return item;
  }
  const parentItem = data.find((parent) => parent.id === item.parent_id);
  return findRootParent(data, parentItem.id);
}

const TreeItem = ({
  item,
  items,
  primaryParentStyles = {},
  childrenStyles = {},
  onSelect = () => {},
  onClose,
  inputValue,
  currentSelectedValue,
  isMulti,
}) => {
  const isDisabled = item.isDisabled;
  const children = items?.filter((child) => child?.parent_id === item?.id);
  const isPrimaryParent = item.parent_id === null;
  const stylesToApply = isPrimaryParent ? primaryParentStyles : childrenStyles;

  const handleSelect = (event) => {
    event.stopPropagation();
    if (isDisabled) return;
    if (onSelect) {
      onSelect(item); // Call onSelect with the selected item
      if (!isMulti) {
        onClose();
      }
    }
  };

  const isSelected = isMulti
    ? currentSelectedValue?.some((value) => value?.id === item?.id)
    : currentSelectedValue?.id === item?.id;

  return (
    <Box {...stylesToApply} onClick={handleSelect}>
      <Flex
        alignItems="center"
        p=".4em .5em"
        gap=".5em"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        _hover={{ bgColor: !isDisabled && '#F6F7F8' }}
        borderRadius="6px"
        opacity={isDisabled ? 0.3 : 1}
      >
        <KText
          size={isPrimaryParent ? 'sm-semi-bold' : 'sm-medium'}
          color={isSelected ? 'primary-600' : 'secondary-700'}
        >
          {item?.name}
        </KText>
        {isSelected && (
          <Check
            size="1em"
            color="var(--kula-colors-primary-600)"
            className="kula-icon"
          />
        )}
      </Flex>
      {children?.length > 0 &&
        children?.map((child) => (
          <TreeItem
            key={child.id}
            item={child}
            inputValue={inputValue}
            items={items}
            onClose={onClose}
            currentSelectedValue={currentSelectedValue}
            primaryParentStyles={primaryParentStyles}
            childrenStyles={childrenStyles}
            onSelect={onSelect}
            isMulti={isMulti}
          />
        ))}
    </Box>
  );
};

const NestedTreeList = ({
  data,
  primaryParentStyles = {
    pr: '1.5em',
  },
  childrenStyles = {
    marginLeft: '10px',
    padding: '.25em .5em',
    borderLeft: '1px solid',
    borderColor: 'border-color',
    cursor: 'pointer',
  },
  onSelect = () => {},
  onClose,
  inputValue,
  currentSelectedValue,
  isMulti,
}) => {
  let itemsToRender = [];
  const allDepartmentsItem = data?.find((item) => item?.id === 'all');

  if (inputValue?.length > 0) {
    const filteredBasedOnSearch = data?.filter((item) =>
      item?.name?.toLowerCase().includes(inputValue.toLowerCase())
    );

    const findParentOfFiltered = filteredBasedOnSearch.map((item) =>
      findRootParent(data, item?.id)
    );

    const uniqueData = findParentOfFiltered.filter(
      (item, index, self) =>
        index === self.findIndex((obj) => obj.id === item.id)
    );
    itemsToRender = uniqueData;
  } else {
    itemsToRender = data?.filter((item) => item?.parent_id === null);
  }

  if (itemsToRender?.length === 0) {
    return (
      <Flex minH="5em" justifyContent="center" alignItems="center">
        <KText>No options found</KText>
      </Flex>
    );
  }
  const isAll = currentSelectedValue?.[0]?.id === 'all';

  return (
    <Box maxH="15em" overflowY="auto">
      {allDepartmentsItem && (
        <Flex
          p=".4em .5em"
          gap=".5em"
          borderRadius="6px"
          fontWeight="bold"
          alignItems="center"
          cursor="pointer"
          _hover={{ bgColor: 'var(--kula-colors-input-fill, #F6F7F8)' }}
          onClick={() => {
            onSelect(allDepartmentsItem);
          }}
        >
          <KText size="sm-semi-bold" color={isAll && 'primary-600'}>
            {allDepartmentsItem?.name}
          </KText>
          {isAll && (
            <Check
              size="1em"
              color="var(--kula-colors-primary-600)"
              className="kula-icon"
            />
          )}
        </Flex>
      )}
      {itemsToRender?.map(
        (item) =>
          item.id !== 'all' && (
            <TreeItem
              key={item?.id}
              item={item}
              items={data}
              onClose={onClose}
              inputValue={inputValue}
              currentSelectedValue={currentSelectedValue}
              primaryParentStyles={primaryParentStyles}
              childrenStyles={childrenStyles}
              onSelect={onSelect}
              isMulti={isMulti}
            />
          )
      )}
    </Box>
  );
};

const getAllNestedChildren = (items, parentId) => {
  let nestedChildren = [];
  const children = items.filter((item) => item.parent_id === parentId);

  children.forEach((child) => {
    nestedChildren.push(child);
    nestedChildren = [
      ...nestedChildren,
      ...getAllNestedChildren(items, child.id),
    ];
  });

  return nestedChildren;
};

export const SelectWithNestedMenu = (props) => {
  const { options = [], selectProps = {}, isMulti } = props;
  const {
    onChange,
    onMenuClose,
    inputValue = '',
    setInputText,
    value,
    extraProps,
  } = selectProps;

  const { valueKey } = extraProps || {};

  const handleSelect = (selectedOption) => {
    let newValue;
    if (isMulti) {
      if (Array.isArray(value)) {
        if (selectedOption.parent_id === null) {
          const nestedChildren = getAllNestedChildren(
            options,
            selectedOption.id
          );
          const allSelected = [...value, selectedOption, ...nestedChildren];

          newValue = allSelected.filter(
            (item, index, self) =>
              index ===
              self.findIndex((obj) => obj[valueKey] === item[valueKey])
          );
        } else {
          const isSelected = value.some(
            (item) => item[valueKey] === selectedOption[valueKey]
          );
          newValue = isSelected
            ? value.filter(
                (item) => item[valueKey] !== selectedOption[valueKey]
              )
            : [...value, selectedOption];
        }
      } else {
        newValue = [selectedOption];
      }
    } else {
      newValue = selectedOption;
    }

    onChange(newValue);
    if (setInputText) setInputText('');
  };

  return (
    <components.Menu {...props}>
      <NestedTreeList
        data={options}
        onSelect={handleSelect}
        onClose={onMenuClose}
        inputValue={inputValue}
        currentSelectedValue={value}
        isMulti={isMulti}
      />
    </components.Menu>
  );
};
